import React from 'react';
import CloumsCenter from '@/pages/intelligence/components/TableComponents/components/CloumsCenter';
import SortBy, {
  SortByItemType,
} from '@/pages/intelligence/components/TableComponents/components/SortBy';
import {
  DATASOURCE_NAME,
  DataSourceBubbleInfo,
} from '@/pages/intelligence/components/DataSourceComponents';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import TableShowData from '@/pages/intelligence/components/TableComponents/components/TableShowData';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import { formatRankValue, formatRankGrowthValue, formatRankGrowthValueInt, formatRankRatioValue } from './utils';
import { LANGUAGE_CN } from '@intelligence/_config/config';
import type { TableColumn } from 'tea-component';
import type {
  IStoreRankMoreCoreRank,
  IStoreRankMoreCoreRankTableColumnRendererType,
  IStoreRankMoreCoreRankTableColumnRenderer,
} from '@/pages/intelligence/types/storeRank';

const TableShowDataRenderer: IStoreRankMoreCoreRankTableColumnRenderer = (
  cvm,
  cfg,
): React.ReactElement => {
  const metrics = cvm?.source_metric_list || [];
  const { width, source, metric, ratioShow } = cfg || {};
  const metricObj = metrics.find((item) => {
    const flag1 = item?.data_source === source;
    const flag2 = item?.metric === metric;
    return flag1 && flag2;
  });
  const growthDataObj = formatRankGrowthValue(metricObj?.growth_ratio);
  return (
    <CloumsCenter width={width} align={'right'}>
      {/* @ts-ignore*/}
      <TableShowData
        isDodHide={!ratioShow}
        maxNumber={'99999+'}
        value={formatRankValue(metricObj?.value)}
        type={growthDataObj?.type}
        growth={growthDataObj?.value}
      />
    </CloumsCenter>
  );
};

const WeiXinTableShowDataRenderer: IStoreRankMoreCoreRankTableColumnRenderer = (
  cvm,
  cfg,
): React.ReactElement => {
  const { width, metric, ratioShow } = cfg || {};
  const growthValue = metric === 'game_rank' ? cvm?.rank_change : cvm?.avg_rank_30d_dod;
  const growthDataObj = formatRankGrowthValueInt(growthValue);
  const rankValue = metric === 'game_rank' ? cvm?.game_rank : cvm?.avg_rank_30d;

  return (
    <CloumsCenter width={width} align={'right'}>
      {/* @ts-ignore*/}
      <TableShowData
        isDodHide={!ratioShow}
        maxNumber={'99999+'}
        value={formatRankRatioValue(rankValue)}
        type={growthDataObj?.type}
        growth={growthDataObj?.value}
      />
    </CloumsCenter>
  );
};

const PureTextRenderer: IStoreRankMoreCoreRankTableColumnRenderer = (
  cvm,
  cfg,
): React.ReactElement => {
  const metrics = cvm?.source_metric_list || [];
  const { width, source, metric } = cfg || {};
  const metricObj = metrics.find((item) => {
    const flag1 = item?.data_source === source;
    const flag2 = item?.metric === metric;
    return flag1 && flag2;
  });
  return (
    <CloumsCenter width={width} align={'right'}>
      {metricObj?.value}
    </CloumsCenter>
  );
};

const TableColumnRenderers: {
  [key in IStoreRankMoreCoreRankTableColumnRendererType]: IStoreRankMoreCoreRankTableColumnRenderer;
} = {
  TableShowDataRenderer,
  PureTextRenderer,
  WeiXinTableShowDataRenderer,
};

export const StoreRankMoreTopColumn = (metadata: {
  locale: string;
  key: string;
  rendererType: IStoreRankMoreCoreRankTableColumnRendererType;
  width?: number;
  widthEN?: number;
  widthCN?: number;
  source: string;
  platform: string;
  metric: string;
  title: string;
  titleContent: React.ReactElement | string;
  ratioShow?: boolean;
  ratioSortable?: boolean;
  currentSortKey: string;
  currentSortValue: string;
  onSortChage: (payload: SortByItemType) => void;
  metricKey?: string;
  entityType?: string;
}): TableColumn<IStoreRankMoreCoreRank> => {
  const {
    locale,
    key,
    rendererType,
    width = 200,
    widthEN,
    widthCN,
    source,
    platform,
    metric,
    title: titleOuter,
    titleContent,
    ratioShow = false,
    ratioSortable = false,
    currentSortKey,
    currentSortValue,
    onSortChage,
    metricKey = '',
    entityType = '',
    genreSource,
  } = metadata ?? {};

  const { formatMessage } = useIntlExtend();

  const title = formatMessage({ id: titleOuter });
  const widthTmp = (locale === LANGUAGE_CN ? widthCN : widthEN) || width;
  const dataSourceIcon = getDataSourceIcon(platform);
  const sortTypes: any[] = [
    {
      key: `${key}_0`,
      sortMetric: metric,
      orderSource: source,
      text: title,
      title,
      ratioType: 0,
    },
  ];
  if (ratioShow && ratioSortable) {
    sortTypes.push({
      key: `${key}_1`,
      sortMetric: metric,
      orderSource: source,
      text: `${title}:${formatMessage({ id: 'dbi_change' })}`,
      title: `${title}(%)`,
      ratioType: 1,
    });
  }
  return {
    key,
    width: widthTmp,
    header: (
      <CloumsCenter width={widthTmp - 20} align={'center'}>
        <SortBy
          title={title as string}
          defaultKey={currentSortKey}
          defaultValue={currentSortValue}
          svgIcon={dataSourceIcon}
          list={sortTypes}
          onChange={onSortChage}
          svgTip={
            <DataSourceBubbleInfo
              metricKey={metricKey}
              entityType={entityType}
              dataOfDom={
                <>
                  <img src={dataSourceIcon} alt="" />
                  {title}
                </>
              }
              comesFormDom={
                <>
                  <img src={dataSourceIcon} alt="" />
                  {DATASOURCE_NAME[platform]}
                </>
              }
              dataSource={platform}
            />
          }
          content={titleContent ? titleContent : undefined}
        />
      </CloumsCenter>
    ),
    align: genreSource === 'weixin' ? 'right' : 'center',
    render: (cvm) => {
      const renderer = TableColumnRenderers?.[rendererType];
      if (!renderer) {
        return (
          <CloumsCenter width={widthTmp} align={'right'}>
            -
          </CloumsCenter>
        );
      }
      return renderer?.(cvm, { width: widthTmp, source, metric, ratioShow });
    },
  };
};
